// src/routes/user-profile-routes.tsx
import { lazy } from 'react';
import type { RoutesProps } from './routes';
import {
  FaUser,
  FaStream,
  FaUsers,
  FaAward,
  FaExclamationTriangle,
  FaImages,
  FaUserShield,
} from 'react-icons/fa';

// Lazy load components
const UserProfile = lazy(() => import('@/pages/management/UserProfile'));
const UserWall = lazy(() => import('@/pages/management/UserWall/UserWall'));
const AllWorksPage = lazy(() => import('@/pages/home/AllWorksPage'));
const AllUsersPage = lazy(() => import('@/pages/home/AllUsersPage'));
const AllCertificatesPage = lazy(() => import('@/pages/home/AllCertificatesPage'));
const AllViolationsPage = lazy(() => import('@/pages/home/AllViolationsPage'));
const WorkDetailPage = lazy(() => import('@/pages/home/WorkDetailPage'));
const UserDetailPage = lazy(() => import('@/pages/home/UserDetailPage'));
const CertificateDetailPage = lazy(() => import('@/pages/home/CertificateDetailPage'));
const ViolationDetailPage = lazy(() => import('@/pages/home/ViolationDetailPage'));
const IdentityProtectionComingSoon = lazy(() => import('@/pages/management/IdentityProtectionComingSoon/IdentityProtectionComingSoon'));

// User profile related routes
export const userProfileRoutes: RoutesProps[] = [
  // Profile Management
  {
    path: "/user/:id", // Xem profile của người dùng khác
    name: "Hồ Sơ Chi Tiết",
    element: <UserProfile />,
    roles: [
      "right_owner",
      "licensee",
      "admin",
      "staff",
      "legal",
      "monitor",
      "guest"
    ],
  },

  // User Wall Routes
  {
    path: "/user/:id",
    element: <UserWall />,
    name: "Tường cá nhân",
    roles: [
      "right_owner",
      "licensee",
      "admin",
      "staff",
      "legal",
      "monitor"
    ],
    icon: FaStream,
  },
  {
    path: "/user/:id/wall",
    element: <UserWall />,
    name: "Tường hoạt động",
    roles: [
      "right_owner",
      "licensee",
      "admin",
      "staff",
      "legal",
      "monitor"
    ],
    icon: FaStream,
  },
  {
    path: "/user/:id/works",
    element: <UserWall initialTab="works" />,
    name: "Tác phẩm của người dùng",
    roles: [
      "right_owner",
      "licensee",
      "admin",
      "staff",
      "legal",
      "monitor"
    ],
  },
  {
    path: "/user/:id/licenses",
    element: <UserWall initialTab="licenses" />,
    name: "Giấy phép của người dùng",
    roles: [
      "right_owner",
      "licensee",
      "admin",
      "staff",
      "legal",
      "monitor"
    ],
  },
  {
    path: "/user/:id/collections",
    element: <UserWall initialTab="collections" />,
    name: "Bộ sưu tập của người dùng",
    roles: [
      "right_owner",
      "licensee",
      "admin",
      "staff",
      "legal",
      "monitor"
    ],
  },
  {
    path: "/user/:id/about",
    element: <UserWall initialTab="about" />,
    name: "Thông tin người dùng",
    roles: [
      "right_owner",
      "licensee",
      "admin",
      "staff",
      "legal",
      "monitor"
    ],
  },

  // Public Discovery Pages
  {
    path: '/works',
    element: <AllWorksPage />,
    name: 'Tất cả tác phẩm',
    roles: ['right_owner', 'licensee', 'admin', 'guest'],
    icon: FaImages
  },
  {
    path: '/users',
    element: <AllUsersPage />,
    name: 'Tác giả nổi bật',
    roles: ['right_owner', 'licensee', 'admin', 'guest'],
    icon: FaUsers
  },
  {
    path: '/certificates',
    element: <AllCertificatesPage />,
    name: 'Giấy chứng nhận',
    roles: ['right_owner', 'admin', 'guest'],
    icon: FaAward
  },
  {
    path: '/violations',
    element: <AllViolationsPage />,
    name: 'Vi phạm bản quyền',
    roles: ['right_owner', 'admin', 'monitor', 'legal', 'guest'],
    icon: FaExclamationTriangle
  },

  // Detail Pages
  {
    path: '/works/:id',
    element: <WorkDetailPage />,
    name: 'Chi tiết tác phẩm',
    roles: ['right_owner', 'licensee', 'admin', 'guest']
  },
  {
    path: '/users/:id',
    element: <UserDetailPage />,
    name: 'Hồ sơ tác giả',
    roles: ['right_owner', 'licensee', 'admin', 'guest']
  },
  {
    path: '/certificates/:id',
    element: <CertificateDetailPage />,
    name: 'Chi tiết chứng nhận',
    roles: ['right_owner', 'admin']
  },
  {
    path: '/violations/:id',
    element: <ViolationDetailPage />,
    name: 'Chi tiết vi phạm',
    roles: ['right_owner', 'admin', 'monitor', 'legal']
  },

  // Identity Protection
  {
    path: "/identity-protection",
    element: <IdentityProtectionComingSoon />,
    name: "Bảo vệ hình ảnh cá nhân",
    roles: ["right_owner", "licensee", "admin", "guest"],
    icon: FaUserShield,
  },
];

// Navigation structure for user profile features
export const userProfileNavigationStructure = {
  publicDiscovery: {
    key: 'public-discovery',
    translationKey: 'publicDiscovery',
    icon: FaUsers,
    roles: ['right_owner', 'licensee', 'admin', 'guest'],
    children: [
      {
        key: 'all-works',
        translationKey: 'allWorks',
        path: '/works',
        icon: FaImages,
        roles: ['right_owner', 'licensee', 'admin', 'guest'],
      },
      {
        key: 'all-users',
        translationKey: 'allUsers',
        path: '/users',
        icon: FaUsers,
        roles: ['right_owner', 'licensee', 'admin', 'guest'],
      },
      {
        key: 'all-certificates',
        translationKey: 'allCertificates',
        path: '/certificates',
        icon: FaAward,
        roles: ['right_owner', 'admin', 'guest'],
      },
      {
        key: 'all-violations',
        translationKey: 'allViolations',
        path: '/violations',
        icon: FaExclamationTriangle,
        roles: ['right_owner', 'admin', 'monitor', 'legal', 'guest'],
      },
    ],
  },
  userWall: {
    key: 'user-wall',
    translationKey: 'userWall',
    icon: FaStream,
    roles: ['right_owner', 'licensee', 'admin', 'staff', 'legal', 'monitor'],
    children: [
      {
        key: 'user-wall',
        translationKey: 'userWall',
        path: '/user/:id/wall',
        icon: FaStream,
        roles: ['right_owner', 'licensee', 'admin', 'staff', 'legal', 'monitor'],
      },
      {
        key: 'user-works',
        translationKey: 'userWorks',
        path: '/user/:id/works',
        icon: FaImages,
        roles: ['right_owner', 'licensee', 'admin', 'staff', 'legal', 'monitor'],
      },
      {
        key: 'user-licenses',
        translationKey: 'userLicenses',
        path: '/user/:id/licenses',
        icon: FaAward,
        roles: ['right_owner', 'licensee', 'admin', 'staff', 'legal', 'monitor'],
      },
      {
        key: 'user-collections',
        translationKey: 'userCollections',
        path: '/user/:id/collections',
        icon: FaImages,
        roles: ['right_owner', 'licensee', 'admin', 'staff', 'legal', 'monitor'],
      },
    ],
  },
  identityProtection: {
    key: 'identity-protection',
    translationKey: 'identityProtection',
    path: '/identity-protection',
    icon: FaUserShield,
    roles: ['right_owner', 'licensee', 'admin', 'guest'],
  },
};

// Helper functions
export const getUserProfileRoutesForRole = (userRoles: string[]): RoutesProps[] => {
  return userProfileRoutes.filter((route) => {
    if (!route.roles || route.roles.length === 0) return true;
    return route.roles.some((role) => userRoles.includes(role));
  });
};

export const hasAccessToPublicDiscovery = (userRoles: string[]): boolean => {
  const allowedRoles = ['right_owner', 'licensee', 'admin', 'guest'];
  return userRoles.some(role => allowedRoles.includes(role));
};

export const hasAccessToUserWall = (userRoles: string[]): boolean => {
  const allowedRoles = ['right_owner', 'licensee', 'admin', 'staff', 'legal', 'monitor'];
  return userRoles.some(role => allowedRoles.includes(role));
};

export default userProfileRoutes;